'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { AdGraphResponseDto } from '@terra/shared-contracts-internal-network';
import type { InternalNetworkAdEntitySummaryDto } from '@terra/shared-contracts-internal-network';
import { InternalNetworkAdEntityKindSchema } from '@terra/shared-schemas-orchestrator';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import {
  Filter,
  IconButton,
  SegmentGroupHorizontal,
  Text,
  Tooltip,
} from '@terra/ui-design-system';
import {
  AlertCircleIcon,
  HierarchyIcon,
  Undo02Icon,
  Search01Icon,
  UnfoldMoreIcon,
} from '@terra/ui-icons';
import { ReactFlowProvider } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  AdPathFiltersPanel,
  InternalNetworkGraphFlow,
  InternalNetworkGraphEdgePanel,
  useAdEdgeFilterSelection,
  type InternalNetworkGraphLayout,
} from '@terra/ui-graphs';
import { useTranslations } from 'next-intl';
import { useTargetId } from '../../store/projectStore';
import { useMainContentTabs } from '../../store/layoutStore';
import { useInternalNetworkExploreGraph } from '../../hooks/api/useInternalNetworkExploreGraph';
import { useInternalNetworkGraphFilters } from '../../hooks/api/useInternalNetworkGraphFilters';
import { InternalNetworkAdEntitySearchCombobox } from '../internal-network/InternalNetworkAdEntitySearchCombobox';
import { EmptyState } from '../EmptyState';
import { buildInternalNetworkAssetTab } from './surface-assets/internal-network-asset-tabs';

export function InternalNetworkGraphTab() {
  const networkId = useTargetId();
  const t = useTranslations('internalNetwork.graph');
  const tControls = useTranslations('internalNetwork.graph.controls');
  const { navigateInActiveTab } = useMainContentTabs();
  const edgeFilters = useAdEdgeFilterSelection();
  const {
    graph,
    isLoading,
    isExpanding,
    isError,
    exploreRootId,
    exploreDepth,
    activePreset,
    totalEntityCount,
    isExploreEmpty,
    expandSelectedNode,
    exploreFromSearch,
    expandMore,
    runPresetQuery,
    resetExplore,
    crownJewelCount,
    ownedPrincipalCount,
  } = useInternalNetworkExploreGraph({
    networkId,
    relationshipKinds: edgeFilters.selectedEdgeTypes,
  });
  const graphFilters = useInternalNetworkGraphFilters({
    activePreset,
    crownJewelCount,
    ownedPrincipalCount,
    onRunPreset: (preset) => {
      void runPresetQuery(preset);
    },
    onResetExplore: resetExplore,
  });
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
  const [focusedNodeId, setFocusedNodeId] = useState<string | null>(null);
  const [layout, setLayout] =
    useState<InternalNetworkGraphLayout>('sequential');
  const [showNodeLabels, setShowNodeLabels] = useState(true);
  const [showEdgeLabels, setShowEdgeLabels] = useState(true);
  const selectedNode = useMemo(() => {
    if (!graph || !selectedNodeId) {
      return null;
    }

    return graph.nodes.find((node) => node.id === selectedNodeId) ?? null;
  }, [graph, selectedNodeId]);
  const selectedEdge = useMemo(() => {
    if (!graph || !selectedEdgeId) {
      return null;
    }

    return graph.edges.find((edge) => edge.id === selectedEdgeId) ?? null;
  }, [graph, selectedEdgeId]);

  useEffect(() => {
    if (!graph) {
      return;
    }

    setShowEdgeLabels(graph.nodes.length <= 30);
    setShowNodeLabels(graph.nodes.length <= 25);
  }, [graph]);
  useEffect(() => {
    if (!graph || graph.nodes.length === 0) {
      setFocusedNodeId(null);

      return;
    }

    if (
      focusedNodeId &&
      !graph.nodes.some((node) => node.id === focusedNodeId)
    ) {
      setFocusedNodeId(null);
    }
  }, [graph, focusedNodeId]);
  const handleSelectNode = useCallback((nodeId: string) => {
    setSelectedNodeId(nodeId);
    setSelectedEdgeId(null);
  }, []);
  const handleViewDetails = useCallback(
    (
      node?: {
        kind: string;
        id: string;
        label?: string;
      } | null,
    ) => {
      const targetNode = node ?? selectedNode;

      if (!targetNode) {
        return;
      }

      const parsedKind = InternalNetworkAdEntityKindSchema.safeParse(
        targetNode.kind,
      );

      if (!parsedKind.success) {
        return;
      }

      setSelectedNodeId(null);
      navigateInActiveTab(
        buildInternalNetworkAssetTab({
          entityKind: parsedKind.data,
          entityId: targetNode.id,
          label: targetNode.label ?? targetNode.id,
        }),
      );
    },
    [navigateInActiveTab, selectedNode],
  );
  const handleSearchEntitySelect = useCallback(
    (entity: InternalNetworkAdEntitySummaryDto) => {
      setSelectedNodeId(entity.nodeId);
      setSelectedEdgeId(null);
      setFocusedNodeId(entity.nodeId);
      void exploreFromSearch(entity.nodeId);
    },
    [exploreFromSearch],
  );
  const normalizedGraph = useMemo((): AdGraphResponseDto | undefined => {
    if (!graph) {
      return undefined;
    }

    return {
      ...graph,
      nodes: graph.nodes.map((node) => ({
        ...node,
        isOwned: node.isOwned ?? false,
      })),
    };
  }, [graph]);

  if (!networkId) {
    return (
      <EmptyState
        icon={HierarchyIcon}
        title={t('noTarget')}
        description={t('noTargetDescription')}
      />
    );
  }

  if (isError) {
    return (
      <EmptyState
        icon={AlertCircleIcon}
        title={t('loadError')}
        description={t('loadErrorDescription')}
      />
    );
  }

  if (!isLoading && totalEntityCount === 0) {
    return (
      <EmptyState
        icon={HierarchyIcon}
        title={t('noDataYet')}
        description={t('noDataYetDescription')}
      />
    );
  }

  const isExploringFromRoot = exploreRootId !== null;
  const hasActiveExploreView = isExploringFromRoot || activePreset !== null;
  const graphNodes = normalizedGraph?.nodes ?? [];
  const emptyDisplayGraph: AdGraphResponseDto = {
    nodes: [],
    edges: [],
    truncated: false,
    totalCount: totalEntityCount,
  };
  const displayGraph = normalizedGraph ?? emptyDisplayGraph;

  return (
    <VStack gap={0} alignItems="stretch" h="full" w="full" minH="500px">
      <Box
        w="full"
        px={4}
        pb={3}
        pt={3}
        borderBottomWidth="1px"
        borderColor="border.muted"
      >
        <HStack
          w="full"
          alignItems="center"
          justifyContent="space-between"
          gap={2.5}
        >
          <HStack gap={2.5} alignItems="center" flex={1} minW={0}>
            <Box w="280px" minW="180px" maxW="280px" flexShrink={0}>
              <InternalNetworkAdEntitySearchCombobox
                networkId={networkId}
                placeholder={t('searchPlaceholder')}
                onSelect={handleSearchEntitySelect}
              />
            </Box>
            {graphFilters.filters.map((filter) => {
              const filterValue = graphFilters.filterValue.find(
                (entry) => entry.id === filter.id,
              )?.value;

              return (
                <Filter
                  key={filter.id}
                  {...filter}
                  value={filterValue}
                  onChange={(newValue) => {
                    const updatedValue = graphFilters.filterValue.filter(
                      (entry) => entry.id !== filter.id,
                    );
                    const nextValue =
                      newValue.length > 0
                        ? [...updatedValue, { id: filter.id, value: newValue }]
                        : updatedValue;

                    graphFilters.onFilterChange(nextValue);
                  }}
                />
              );
            })}
          </HStack>
          <HStack gap={2} flexShrink={0}>
            <SegmentGroupHorizontal
              options={[
                { value: 'sequential', label: tControls('layoutSequential') },
                { value: 'standard', label: tControls('layoutRadial') },
              ]}
              value={layout}
              onValueChange={({ value }) => {
                if (value === 'sequential' || value === 'standard') {
                  setLayout(value);
                }
              }}
            />
            {hasActiveExploreView ? (
              <>
                {isExploringFromRoot ? (
                  <Tooltip title="Expand more">
                    <IconButton
                      size="sm"
                      variant="ghost"
                      loading={isExpanding}
                      disabled={exploreDepth >= 3}
                      aria-label="Expand more"
                      onClick={() => {
                        if (exploreRootId) {
                          setFocusedNodeId(exploreRootId);
                        }

                        void expandMore();
                      }}
                    >
                      <UnfoldMoreIcon size={16} />
                    </IconButton>
                  </Tooltip>
                ) : null}
                <Tooltip title="Reset explore">
                  <IconButton
                    size="sm"
                    variant="ghost"
                    aria-label="Reset explore"
                    onClick={() => void resetExplore()}
                  >
                    <Undo02Icon size={16} />
                  </IconButton>
                </Tooltip>
              </>
            ) : null}
          </HStack>
        </HStack>
      </Box>

      {displayGraph.truncated && graphNodes.length > 0 ? (
        <Box px={4} py={2}>
          <Text fontSize="xs" color="fg.muted">
            {t('showingNodes', {
              shown: graphNodes.length,
              total: displayGraph.totalCount,
            })}
          </Text>
        </Box>
      ) : null}

      <Box flex={1} minH={0} position="relative">
        {isLoading && graphNodes.length === 0 ? (
          <EmptyState
            icon={HierarchyIcon}
            title={t('loadingGraph')}
            isLoading
          />
        ) : isExploreEmpty ? (
          <EmptyState
            icon={Search01Icon}
            title={t('startExploring')}
            description={t('startExploringDescription')}
          />
        ) : (
          <ReactFlowProvider>
            <Box
              position="absolute"
              top={3}
              left={3}
              zIndex={5}
              maxH="calc(100% - 24px)"
            >
              <AdPathFiltersPanel
                taxonomy={edgeFilters.taxonomy}
                selected={edgeFilters.selected}
                isDefaultSelection={edgeFilters.isDefaultSelection}
                disabled={
                  !isExploringFromRoot &&
                  (activePreset === null || activePreset === 'crown-jewels')
                }
                onToggleEdgeType={edgeFilters.toggleEdgeType}
                onSetSubcategorySelected={edgeFilters.setSubcategorySelected}
                onSelectAll={edgeFilters.selectAll}
                onClearAll={edgeFilters.clearAll}
                onResetToDefault={edgeFilters.resetToDefault}
              />
            </Box>
            <InternalNetworkGraphFlow
              graph={displayGraph}
              layout={layout}
              showNodeLabels={showNodeLabels}
              showEdgeLabels={showEdgeLabels}
              focusedNodeId={focusedNodeId}
              selectedNodeId={selectedNodeId}
              selectedEdgeId={selectedEdgeId}
              isExpanding={isExpanding}
              onSelectNode={(nodeId, _entityKind) => {
                handleSelectNode(nodeId);
              }}
              onExpandNode={(nodeId) => {
                void expandSelectedNode(nodeId).then((expanded) => {
                  if (expanded) {
                    setFocusedNodeId(nodeId);
                  }
                });
              }}
              onViewDetails={(nodeId) => {
                const node = graph?.nodes.find((item) => item.id === nodeId);

                if (node) {
                  handleViewDetails({
                    kind: node.kind,
                    id: node.id,
                    label: node.label,
                  });
                }
              }}
              onSelectEdge={(edgeId) => {
                setSelectedEdgeId(edgeId);
                setSelectedNodeId(null);
              }}
              onClearSelection={() => {
                setSelectedNodeId(null);
                setSelectedEdgeId(null);
              }}
              onShowNodeLabelsChange={setShowNodeLabels}
              onShowEdgeLabelsChange={setShowEdgeLabels}
            />
            {selectedEdge ? (
              <InternalNetworkGraphEdgePanel
                edge={selectedEdge}
                onClose={() => setSelectedEdgeId(null)}
              />
            ) : null}
          </ReactFlowProvider>
        )}
      </Box>
    </VStack>
  );
}
